import {AfterViewInit, Component, ElementRef, Input, ViewChild, HostListener, OnInit} from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';

import * as THREE from 'three';

import {DataService} from '../data.service';
import {ShelfElement} from '../shelf-element';
import { ShelfElementService } from '../shelf-element.service';
import {KklRestService} from '../kkl-rest-service.service';

import { BaseElement } from '../base-element';
import { ERASER } from '../eraser';
import { Room } from '../room';
import { ElementListComponent } from '../element-list/element-list.component';

import { ShelfSection} from '../shelf-section';

import '../js/EnableThreeExamples';
import 'three/examples/js/controls/OrbitControls';
import 'three/examples/js/loaders/ColladaLoader';


@Component({
  selector: 'app-scene',
  templateUrl: './scene.component.html',
  styleUrls: ['./scene.component.css']
})
export class SceneComponent implements OnInit    {

    @Input() room: Room;

    private shelfElements: ShelfElement[];

    private eraser: BaseElement;

    private renderer: THREE.WebGLRenderer;
    private camera: THREE.PerspectiveCamera;
    private scene: THREE.Scene;

    private rolloverMesh: THREE.Mesh;
    private rolloverMeshSideWall: THREE.Mesh;

    private raycaster: THREE.Raycaster;

    private roomLength: number;
    private roomWidth: number;
    private roomHeight: number;

    public roomIsDisplayed = false;
    public displayShowElementListButton = false;

    private leftWallView: boolean;
    private rightWallView: boolean;

    /**
     * @var ShelfElement
     */
    public activeElement;

    public fieldOfView: 60;
    public nearClippingPane: 1;
    public farClippingPane: 1000;

    /**
     * @var THREE.ColladaLoader
     */
    private colladaLoader = THREE.ColladaLoader;

    private objects = [];
    private availableElementsMap = new Map();
    private hoverElementsMap = new Map();

    private shelfSectionsX = new Map();
    private shelfSectionsZ = new Map();

    private insertedElemntToArticleMap = new Map();

    private elementWidthInX = 56.15;
    private numberOfshelfSectionsInX: number;
    private maxAllowedXPosition: number;

    private elementWidthInZ = 56.15;
    private numberOfshelfSectionsInZ: number;
    private maxAllowedZPosition: number;

    private eraser3dObject;
    private eraserIsInScene: boolean;
    private eraserIsActive: boolean;
    private markedObject;



    private insertedElements = [];
    private insertedElementsSideWall = [];

    public elementCountList = new Map();

    public onFormPage: boolean;
    public showConfirmation: boolean;
    public validationErrors: boolean;

    private font: THREE.Font;

    private shelfHeight = 0.0;

    private shelfHeightLineElements = new Map();

    @ViewChild('canvas')
    private canvasRef: ElementRef;

    @ViewChild('elementListOverlay')
    private elementListOverlayControlRef: ElementRef;

    @ViewChild(ElementListComponent)
    private elementListComponent;

    @ViewChild('appElementList')
    private elementListRef: ElementRef;

    @ViewChild('appSubmitForm')
    private submitFormRef: ElementRef;

    /**
     * @var boolean
     */
    private showElementListControl = false;

    /**
     * @var boolean
     */
    public showStartOverlay = true;

    /**
     * @var boolean
     */
    public showLoadOverlay = false;

    /**
     * @var boolean
     */
    public allModulesLoaded = false;

    /**
     * @var boolean
     */
    public formSubmitSent = false;

    /**
     * @var number
     */
    public currentStep = 1;

    /**
     * @var boolean
     */
    public eraserIsLoaded = false;

    /**
     * @var boolean
     */
    public validationErrorFound = false;

    /**
     * @var FormGroup
     */
    public roomForm;

    /**
     * @param {DataService} dataService
     * @param {ShelfElementService} shelfElementService
     * @param {KklRestService} kklRestService
     */
    constructor(private dataService: DataService, private shelfElementService: ShelfElementService, private kklRestService: KklRestService) {}

    private get canvas(): HTMLCanvasElement {
        return this.canvasRef.nativeElement;
    }

    private get elementListOverlay(): HTMLElement {
        return this.elementListOverlayControlRef.nativeElement;
    }

    getShelfElements(): void {
        const self = this;
        this.shelfElementService.getShelfElements().subscribe(shelfElements => {
            this.shelfElements = shelfElements;
            this.shelfElements.forEach(function(element) {
                if (!self.elementCountList.has(element.articleNumber)) {
                    self.elementCountList.set(element.articleNumber, 0);
                }
            });
        });
    }

    getNonFloorShelfElements(): void {
        const self = this;
        this.shelfElementService.getNonFloorShelfElements().subscribe(shelfElements => {
            this.shelfElements = shelfElements;
            this.shelfElements.forEach(function(element) {
                if (!self.elementCountList.has(element.articleNumber)) {
                    self.elementCountList.set(element.articleNumber, 0);
                }
            });
        });
    }

    getFloorShelfElements(): void {
        const self = this;
        this.shelfElementService.getFloorShelfElements().subscribe(shelfElements => {
            this.shelfElements = shelfElements;
            this.shelfElements.forEach(function(element) {
                if (!self.elementCountList.has(element.articleNumber)) {
                    self.elementCountList.set(element.articleNumber, 0);
                }
            });
        });
    }

    getEraser() {
        this.eraser = ERASER;
    }

    ngOnInit() {
        this.getRoom();

        this.roomForm = new FormGroup({
            'length': new FormControl(this.room.length, [
                Validators.required,
                Validators.min(2),
                Validators.max(10),
                Validators.pattern('[0-9]+[\,\.]?[0-9]*')
                ]),
            'width': new FormControl(this.room.width, [
                Validators.required,
                Validators.min(2),
                Validators.max(10),
                Validators.pattern('[0-9]+[\,\.]?[0-9]*')
            ]),
            'height': new FormControl(this.room.height, [
                Validators.required,
                Validators.min(1),
                Validators.max(2.5),
                Validators.pattern('[0-9]+[\,\.]?[0-9]*')
            ])
            });

        this.font = null;
        this.currentStep = 1;
        this.dataService.shareData('currentStep', this.currentStep);
        this.dataService.shareData('scene', this);
    }

    get width() {
        return this.roomForm.get('width');
    }

    get length() {
        return this.roomForm.get('length');
    }

    get height() {
        return this.roomForm.get('height');
    }

    ngAfterViewInit() {
        this.getFloorShelfElements();
        this.getEraser();
        this.loadFont();
        this.load3dModules(function() {});
    }

    public start3dApp() {
        if (typeof this.length.value === 'string') {
            this.length.value = this.length.value.replace(',', '.');
            this.length.value = parseFloat(this.length.value);
        }
        this.room.length = this.length.value;

        if (typeof this.width.value === 'string') {
            this.width.value = this.width.value.replace(',', '.');
            this.width.value = parseFloat(this.width.value);
        }
        this.room.width = this.width.value;

        if (typeof this.height.value === 'string') {
            this.height.value = this.height.value.replace(',', '.');
            this.height.value = parseFloat(this.height.value);
        }
        this.room.height = this.height.value;

        this.showStartOverlay = false;
        this.showRoom();

    }

    public prepareNextStep() {
        this.getNonFloorShelfElements();
        const self = this;
        if (this.allModulesLoaded === false) {
            this.showLoadOverlay = true;
            this.load3dModules(function() {
                setTimeout(function() {
                    self.showLoadOverlay = false;
                    self.allModulesLoaded  = true;
                }, 9000);
            });
        }
    }

    /**
     * @return void
     */
    private getRoom(): void {
        this.room = new Room();
        this.room.length = 4;
        this.room.width = 4;
        this.room.height = 2.5;
    }

    private load3dModules(callback) {
        this.colladaLoader = new THREE.ColladaLoader();
        const self = this;
        for (const index in this.shelfElements) {
            const shelfElement = this.shelfElements[index];

            if (!self.availableElementsMap.has(shelfElement.articleNumber)) {
                this.colladaLoader.load('assets/models/' + shelfElement.model3d, function(dae) {
                    self.availableElementsMap.set(shelfElement.articleNumber, dae);
                });

                // load 2 times in lack of good in depth copy for javascript objects
                this.colladaLoader.load('assets/models/' + shelfElement.model3d, function(dae) {
                    self.hoverElementsMap.set(shelfElement.articleNumber, dae);
                });
            }
        }

        if (!self.eraserIsLoaded) {
            this.colladaLoader.load('assets/models/' + this.eraser.model3d, function(dae) {
                self.eraser3dObject = dae.scene;
                self.eraserIsLoaded = true;
            });
        }

        callback();
    }

    private loadFont() {
        let self = this;
        let loader = new THREE.FontLoader();
        loader.load( 'assets/fonts/helvetiker_regular.typeface.json', function ( font ) {
            self.font = font;
        });
    }

    public showRoom() {
        this.leftWallView = false;
        this.rightWallView = false;
        this.createSceneAndRender();
        this.roomIsDisplayed = true;
    }

    public createSceneAndRender() {
      if (this.scene === undefined) {
          this.createScene();
      }
      if (this.camera === undefined) {
          this.createCamera();
      } else {
          // look at initial position
          this.camera.position.set( -this.roomLength * 1.4, this.roomHeight / 2 , this.roomWidth * 1.4 );
          this.camera.lookAt( - this.roomLength / 2, this.roomHeight / 2 , this.roomWidth / 2 );
      }
      if (this.renderer === undefined) {
          this.initRenderer();
      }
      this.render();
    }


    private addLineToScene(direction, offset, height) {

        if (direction === 'x') {
            const geomentry = new THREE.Geometry();
            geomentry.vertices.push(new THREE.Vector3(0 , height, offset));
            geomentry.vertices.push(new THREE.Vector3(-this.roomLength , height, offset));
            const line = new THREE.Line( geomentry, new THREE.MeshBasicMaterial( { visible: true, color: 0x040404} ))
            this.scene.add(line);
        }

        if (direction === 'y') {
            const geometry = new THREE.Geometry();
            geometry.vertices.push(new THREE.Vector3(offset , height, 0));
            geometry.vertices.push(new THREE.Vector3(offset, height, this.roomWidth));
            const line = new THREE.Line( geometry, new THREE.MeshBasicMaterial( { visible: true, color: 0x040404} ))
            this.scene.add(line);
        }

    }

    private addLineToWall(xPosition, zPosition) {
        const geometry = new THREE.Geometry();
        geometry.vertices.push(new THREE.Vector3(xPosition , 0, zPosition));
        geometry.vertices.push(new THREE.Vector3(xPosition, this.roomHeight, zPosition));
        const line = new THREE.Line( geometry, new THREE.MeshBasicMaterial( { visible: true, color: 0xb2b2b2} ))
        this.scene.add(line);
    }


    private addLabelToWall(label, xPosition, zPosition, wall = 'x') {

        const height = this.roomHeight + 10;

        const textGeo = new THREE.TextGeometry( label, {
            font: this.font,
            size: 7,
            height: 1,
            curveSegments: 12,
            bevelEnabled: false,
        } );

        textGeo.computeBoundingBox();
        textGeo.computeVertexNormals();

        const materials = [
            new THREE.MeshPhongMaterial( { color: 0x333333, flatShading: true } ), // front
            new THREE.MeshPhongMaterial( { color: 0x333333 } ) // side
        ];

        let textMesh = new THREE.Mesh( textGeo, materials );
        textMesh.position.x = xPosition;
        textMesh.position.y = height;
        textMesh.position.z = zPosition;

        if (wall === 'x') {
            this.scene.add(textMesh);
        }

        if (wall === 'z') {
            textMesh.rotateY( - Math.PI / 2 );
            this.scene.add(textMesh);
        }

    }


    private createScene() {
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color( 0xf0f0f0 );

        if (this.room.height > 2.5) {
            this.room.height = 2.5;
        }
        if (this.room.width > 10) {
            this.room.width = 10;
        }
        if (this.room.length > 10) {
            this.room.length = 10;
        }

        this.roomLength = this.room.length * 100; // x
        this.roomWidth = this.room.width * 100; // z
        this.roomHeight = this.room.height * 100; // y

        this.numberOfshelfSectionsInX = Math.floor(this.roomLength / this.elementWidthInX);
        this.maxAllowedXPosition = -this.elementWidthInX * this.numberOfshelfSectionsInX - (62.3 - this.elementWidthInX );
        this.numberOfshelfSectionsInZ = Math.floor(this.roomWidth / this.elementWidthInZ);
        this.maxAllowedZPosition = this.elementWidthInZ * (this.numberOfshelfSectionsInZ - 1) + (62.3 - this.elementWidthInZ );

        const geometry = new THREE.PlaneBufferGeometry( this.roomLength, this.roomWidth );
        geometry.rotateX( - Math.PI / 2 );
        const plane = new THREE.Mesh( geometry, new THREE.MeshBasicMaterial( { visible: true, color: 0xd3d3d3 } ) );
        plane.name = 'floor';
        plane.position.set(-this.roomLength / 2, 0, this.roomWidth / 2);

        this.scene.add( plane );
        this.objects.push( plane );

        const geometry2 = new THREE.PlaneBufferGeometry( this.roomLength, this.roomHeight );
        const plane2 = new THREE.Mesh( geometry2, new THREE.MeshBasicMaterial( { visible: true, color: 0xfdf5e6 } ) );
        plane2.position.set(-this.roomLength / 2, this.roomHeight / 2, 0);
        plane2.name = 'back-wall';
        this.scene.add( plane2 );
        this.objects.push( plane2 );

        const geometry3 = new THREE.PlaneBufferGeometry( this.roomWidth, this.roomHeight );
        geometry3.rotateY( -Math.PI / 2 );
        const plane3 = new THREE.Mesh( geometry3, new THREE.MeshBasicMaterial( { visible: true, color: 0xffefd5 } ) );
        plane3.position.set(0, this.roomHeight / 2, this.roomWidth / 2);
        plane3.name = 'side-wall';
        this.scene.add( plane3 );
        this.objects.push( plane3 );

        for ( let i = 62.30; i < this.roomWidth; i += 56.15) {
            this.addLineToScene('x', i, 0);
            this.addLineToWall(-1, i);
            this.addLabelToWall( ( Math.ceil( i ) / 100 ) + ' m', -1, i, 'z');
        }

        for ( let i = -62.30; i > -this.roomLength; i -= 56.15) {
            this.addLineToScene('y', i, 0);
            this.addLineToWall(i, 1);
            this.addLabelToWall( ( Math.ceil( -i ) / 100 ) + ' m', i, 1);
        }

        this.raycaster = new THREE.Raycaster();
        const light = new THREE.AmbientLight(0xffffff);
        const directionalLight = new THREE.DirectionalLight( 0xffffff, 0.9 );
        this.scene.add(directionalLight);
        this.scene.add(light);
    }


    private addHeightLinesToScene(height, label) {

        if (this.shelfHeightLineElements.has(height)) {
            return;
        }

        const geometry4 = new THREE.Geometry();
        geometry4.vertices.push(new THREE.Vector3(0 , height, 1));
        geometry4.vertices.push(new THREE.Vector3(-this.roomLength , height, 1));
        const line = new THREE.Line( geometry4, new THREE.MeshBasicMaterial( { visible: true, color: 0xb2b2b2} ))
        this.scene.add(line);

        const geometry5 = new THREE.Geometry();
        geometry5.vertices.push(new THREE.Vector3(-1 , height, 1));
        geometry5.vertices.push(new THREE.Vector3(-1, height, this.roomWidth));
        const line2 = new THREE.Line( geometry5, new THREE.MeshBasicMaterial( { visible: true, color: 0xb2b2b2} ))
        this.scene.add(line2);

        let textMesh1 = null;
        let textMesh2 = null;

        if (this.font !== null) {
            const textGeo = new THREE.TextGeometry( label, {
                font: this.font,
                size: 7,
                height: 1,
                curveSegments: 12,
                bevelEnabled: false,
            } );

            textGeo.computeBoundingBox();
            textGeo.computeVertexNormals();

            const materials = [
                new THREE.MeshPhongMaterial( { color: 0x000000, flatShading: true } ), // front
                new THREE.MeshPhongMaterial( { color: 0x000000 } ) // side
            ];

            textMesh1 = new THREE.Mesh( textGeo, materials );

            textMesh1.position.x = - (this.roomLength + 30);
            textMesh1.position.y = height;
            textMesh1.position.z = 2;

            this.scene.add(textMesh1);

            textMesh2 = new  THREE.Mesh( textGeo, materials );
            textMesh2.position.z = this.roomWidth + 10;
            textMesh2.position.y = height;
            textMesh2.position.x = 10;
            textMesh2.rotateY( - Math.PI / 2 );
            this.scene.add(textMesh2);
        }

        this.shelfHeightLineElements.set(height, [ line, line2, textMesh1, textMesh2]);
    }

    private findHightOfHighestShelfSection() {
        let maxHeight = 0;
        for (const entry of Array.from(this.shelfSectionsX.entries())) {
            const key = entry[0];
            const shelfSection = entry[1];
            const sectionHeight = shelfSection.height;
            if (sectionHeight > maxHeight) {
                maxHeight = sectionHeight;
            }
        }
        for (const entry of Array.from(this.shelfSectionsZ.entries())) {
            const key = entry[0];
            const shelfSection = entry[1];
            const sectionHeight = shelfSection.height;

            if (sectionHeight > maxHeight) {
                maxHeight = sectionHeight;
            }
        }
        return maxHeight;
    }


    private createCamera() {
        let aspectRatio;
        if (this.canvas.clientHeight !== 0) {
            aspectRatio = this.canvas.clientWidth / this.canvas.clientHeight;
        } else  {
            aspectRatio = 0;
        }
        this.camera = new THREE.PerspectiveCamera(
            this.fieldOfView,
            aspectRatio,
            this.nearClippingPane,
            this.farClippingPane
        );

        this.camera.position.set( -this.roomLength * 1.4, this.roomHeight / 2 , this.roomWidth * 1.4 );
        this.camera.lookAt( - this.roomLength / 2, this.roomHeight / 2 , this.roomWidth / 2 );
    }


    private addrolloverMesh(activeElement) {

        const elementDae = this.hoverElementsMap.get(activeElement.articleNumber);

        const elementObject = elementDae.scene.clone();
        for (const arrayIndex in elementObject.children) {
            let childObject = elementObject.children[arrayIndex];
            if (childObject.type === 'Mesh') {
                childObject.material.transparent = true;
                childObject.material.opacity = 0.2;
            }
        }

        elementObject.scale.set(100, 100, 100);
        elementObject.rotateX(  Math.PI / 2 );
        elementObject.rotateY(  Math.PI  );

        this.rolloverMesh = elementObject;
        this.rolloverMesh.position.set( 200 , 0 , 0 );


        this.objects.push( this.rolloverMesh );
        this.scene.add( this.rolloverMesh );
    }

    private addrolloverMeshSideWall() {

        const elementObject = this.rolloverMesh.clone();
        elementObject.rotateY(  - Math.PI / 2 );
        this.rolloverMeshSideWall = elementObject;
        this.rolloverMeshSideWall.position.set( - this.activeElement.length, 0 , - 200);
        this.objects.push( this.rolloverMeshSideWall );
        this.scene.add( this.rolloverMeshSideWall );
    }

    private addEraser() {
        this.eraser3dObject.scale.set(25, 25, 25);
        this.eraser3dObject.position.set(-200, 100, 100);
        this.eraser3dObject.rotateY(  - Math.PI / 4 );
        this.eraser3dObject.visible = true;
        this.scene.add( this.eraser3dObject );
        this.eraserIsInScene  = true;
    }

    private initRenderer() {
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: true
        });
        this.renderer.setPixelRatio(devicePixelRatio);
        this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);

        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.setClearColor(0xffffff, 1);
        this.renderer.autoClear = true;

    }

    public render() {
        this.renderer.render(this.scene, this.camera);
    }

    private removeElement(inputArray, element) {
        for (let i = 0; i < inputArray.length; i++) {
            const arrayElement = inputArray[i];
            if (element.uuid === arrayElement.uuid) {
                inputArray.splice(i, 1);
            }
        }
        return inputArray;
    }

    public onMouseDown(event) {

        if (this.scene !== undefined && this.activeElement !== undefined) {
            const mouse = new THREE.Vector2();
            const x = ( (event.clientX - this.canvas.offsetLeft) / this.canvas.clientWidth ) * 2 - 1;
            const y =  - ( (event.clientY - this.canvas.offsetTop) / this.canvas.clientHeight ) * 2 + 1;
            mouse.set(x, y);

            this.raycaster.setFromCamera( mouse, this.camera );
            const intersects = this.raycaster.intersectObjects( this.objects );
            if ( intersects.length > 0 ) {
                for (let i = 0; i < intersects.length ; i++) {
                    const intersect = intersects[ i ];
                    if (intersect.object.name === 'back-wall') {
                        if (this.eraserIsActive) {

                            const shelfSectionNumberX = this.calculateShelfSectionNumberForTargetXPosition(this.eraser3dObject.position.x, this.markedObject);
                            //console.log(shelfSectionNumberX);

                            if (this.shelfSectionsX.has(shelfSectionNumberX)) {
                                const currentShelfSection = this.shelfSectionsX.get(shelfSectionNumberX);
                                const currentShelfHeight = currentShelfSection.height;
                                let currentIstTopElement = false;
                                if (currentShelfHeight === this.shelfHeight) {
                                    currentIstTopElement = true;
                                }
                                currentShelfSection.removeElement();
                                currentShelfSection.isClosed = false;
                                this.shelfSectionsX.set(shelfSectionNumberX, currentShelfSection);

                                this.shelfHeight = this.findHightOfHighestShelfSection();
                                if (currentIstTopElement) {
                                    if (this.shelfHeight === currentShelfSection.height) {
                                        // reduced Element is highst element now: remove line
                                        const lineElements = this.shelfHeightLineElements.get(currentShelfHeight);
                                        for (const element of lineElements) {
                                            this.scene.remove(element);
                                        }
                                        this.shelfHeightLineElements.delete(currentShelfHeight);
                                    }
                                }
                            }

                            this.scene.remove(this.markedObject);
                            this.insertedElements = this.removeElement(this.insertedElements, this.markedObject);
                            const markedArticleNumber = this.insertedElemntToArticleMap.get(this.markedObject.uuid);

                            if (this.elementCountList.has(markedArticleNumber)) {
                                let elementCount = this.elementCountList.get(markedArticleNumber);
                                if (elementCount > 1 ) {
                                    elementCount --;
                                    this.elementCountList.set(markedArticleNumber, elementCount);
                                } else {
                                    this.elementCountList.delete(markedArticleNumber);
                                }
                            }
                        } else {

                            if (this.rolloverMesh.visible) {
                                const targetX = this.rolloverMesh.position.x;
                                const targetY = this.rolloverMesh.position.y;
                                const targetZ = this.rolloverMesh.position.z;

                                const shelfSectionNumberX = this.calculateShelfSectionNumberForTargetXPosition(targetX, this.activeElement);
                                let currentShelfSection;

                                if (this.shelfSectionsX.has(shelfSectionNumberX)) {
                                    currentShelfSection = this.shelfSectionsX.get(shelfSectionNumberX);
                                } else {
                                    currentShelfSection = new ShelfSection();
                                    currentShelfSection.number = shelfSectionNumberX;
                                    currentShelfSection.xPosition = targetX;
                                    currentShelfSection.zPosition = targetZ;
                                }
                                currentShelfSection.addElement(this.activeElement);

                                currentShelfSection.height = currentShelfSection.height + this.activeElement.height;
                                if (this.shelfHeight < currentShelfSection.height) {
                                    this.shelfHeight = currentShelfSection.height;
                                    this.addHeightLinesToScene(this.shelfHeight, ( Math.round(this.shelfHeight) / 100) + ' m');
                                }

                                if (this.activeElement.isTopElement) {
                                    currentShelfSection.isClosed = true;
                                } else {
                                    currentShelfSection.isClosed = false;
                                }

                                this.shelfSectionsX.set(shelfSectionNumberX, currentShelfSection);

                                const elementDae = this.availableElementsMap.get(this.activeElement.articleNumber);
                                const elementObject = elementDae.scene.clone();

                                elementObject.scale.set(100, 100, 100);
                                elementObject.rotateX(  Math.PI / 2 );
                                elementObject.rotateY(  Math.PI  );

                                elementObject.position.set(targetX, targetY, targetZ);
                                this.objects.push( elementObject );
                                this.scene.add( elementObject );
                                this.insertedElements.push(elementObject);
                                this.insertedElemntToArticleMap.set(elementObject.uuid, this.activeElement.articleNumber);

                                let elementCount = 1;
                                if (this.elementCountList.has(this.activeElement.articleNumber)) {
                                    elementCount = this.elementCountList.get(this.activeElement.articleNumber);
                                    elementCount ++;
                                }

                                this.elementCountList.set(this.activeElement.articleNumber, elementCount);


                                if (! this.showElementListControl) {
                                    this.displayShowElementListButton = true;
                                    this.showElementListControl = true;
                                }
                            }


                        }
                    }

                    if (intersect.object.name === 'side-wall') {

                        if (this.eraserIsActive) {

                            const shelfSectionNumberZ = this.calculateShelfSectionNumberForTargetZPosition(this.eraser3dObject.position.z);

                            //console.log(shelfSectionNumberZ);
                            if (this.shelfSectionsZ.has(shelfSectionNumberZ)) {
                                const currentShelfSection = this.shelfSectionsZ.get(shelfSectionNumberZ);
                                const currentShelfHeight = currentShelfSection.height;
                                let currentIstTopElement = false;
                                if (currentShelfHeight === this.shelfHeight) {
                                    currentIstTopElement = true;
                                }
                                const removedElement = currentShelfSection.removeElement();
                                currentShelfSection.height = currentShelfSection.height - removedElement.height;
                                currentShelfSection.isClosed = false;
                                this.shelfSectionsZ.set(shelfSectionNumberZ, currentShelfSection);
                                this.shelfHeight = this.findHightOfHighestShelfSection();
                                if (currentIstTopElement) {
                                    if (this.shelfHeight === currentShelfSection.height) {
                                        // reduced Element is highst element now: remove line
                                        const lineElements = this.shelfHeightLineElements.get(currentShelfHeight);
                                        for (const element of lineElements) {
                                            this.scene.remove(element);
                                        }
                                        this.shelfHeightLineElements.delete(currentShelfHeight);
                                    }
                                }
                            }

                            this.scene.remove(this.markedObject);
                            this.insertedElementsSideWall = this.removeElement(this.insertedElementsSideWall, this.markedObject);
                            const markedArticleNumber = this.insertedElemntToArticleMap.get(this.markedObject.uuid);
                            if (this.elementCountList.has(markedArticleNumber)) {
                                let elementCount = this.elementCountList.get(markedArticleNumber);
                                if (elementCount > 0 ) {
                                    elementCount --;
                                    this.elementCountList.set(markedArticleNumber, elementCount);
                                }
                            }
                        } else {
                            const targetX = this.rolloverMeshSideWall.position.x;
                            const targetY = this.rolloverMeshSideWall.position.y;
                            const targetZ = this.rolloverMeshSideWall.position.z;

                            const shelfSectionNumberZ = this.calculateShelfSectionNumberForTargetZPosition(targetZ);
                            let currentShelfSection;

                            if (this.shelfSectionsZ.has(shelfSectionNumberZ)) {
                                currentShelfSection = this.shelfSectionsZ.get(shelfSectionNumberZ);
                            } else {
                                currentShelfSection = new ShelfSection();
                                currentShelfSection.number = shelfSectionNumberZ;
                                currentShelfSection.xPosition = targetX;
                                currentShelfSection.zPosition = targetZ;
                            }
                            currentShelfSection.addElement(this.activeElement);

                            currentShelfSection.height = currentShelfSection.height + this.activeElement.height;
                            if (this.shelfHeight < currentShelfSection.height) {
                                this.shelfHeight = currentShelfSection.height;
                                this.addHeightLinesToScene(this.shelfHeight, ( Math.round(this.shelfHeight) / 100) + ' m');
                            }


                            if (this.activeElement.isTopElement) {
                                currentShelfSection.isClosed = true;
                            } else {
                                currentShelfSection.isClosed = false;
                            }
                            this.shelfSectionsZ.set(shelfSectionNumberZ, currentShelfSection);

                            const elementDae = this.availableElementsMap.get(this.activeElement.articleNumber);
                            const elementObject = elementDae.scene.clone();

                            elementObject.scale.set(100, 100, 100);
                            elementObject.rotateX(  Math.PI / 2 );
                            elementObject.rotateY(  Math.PI / 2 );

                            elementObject.position.set(targetX, targetY, targetZ);
                            this.objects.push( elementObject );
                            this.scene.add( elementObject );
                            this.insertedElementsSideWall.push(elementObject);
                            this.insertedElemntToArticleMap.set(elementObject.uuid, this.activeElement.articleNumber);

                            let elementCount = 1;
                            if (this.elementCountList.has(this.activeElement.articleNumber)) {
                                elementCount = this.elementCountList.get(this.activeElement.articleNumber);
                                elementCount ++;
                            }
                            this.elementCountList.set(this.activeElement.articleNumber, elementCount);

                            if (! this.showElementListControl) {
                                this.displayShowElementListButton = true;
                                this.showElementListControl = true;
                            }
                        }
                    }
                }
            }
            this.render();
        }
    }

    public onMouseMove(event) {

        const activeElement = this.dataService.getSharedData('activeElement');
        this.eraserIsActive = false;
        if (activeElement !== undefined) {
            if (activeElement.name === 'Eraser') {
                if (!this.eraserIsInScene) {
                    this.addEraser();
                }
                this.eraserIsActive = true;
            }
        }

        if (activeElement !== this.activeElement) {
            this.activeElement = activeElement;
            this.scene.remove(this.rolloverMesh);
            this.scene.remove(this.rolloverMeshSideWall);
            if (!this.eraserIsActive) {
                if (this.eraser3dObject) {
                    this.eraser3dObject.visible = false;
                }
                this.addrolloverMesh(activeElement);
                this.addrolloverMeshSideWall();
            }
        }

        if ( (this.scene !== undefined) && ( this.rolloverMesh !== undefined) && ( this.activeElement !== undefined) ) {

            // console.log(activeElement);
            const mouse = new THREE.Vector2();
            const x = ((event.clientX - this.canvas.offsetLeft) / this.canvas.clientWidth) * 2 - 1;
            const y = -((event.clientY - this.canvas.offsetTop) / this.canvas.clientHeight) * 2 + 1;
            mouse.set(x, y);
            this.raycaster.setFromCamera(mouse, this.camera);
            const intersects = this.raycaster.intersectObjects(this.objects);

            if (intersects.length > 0) {
                for (let i = 0; i < intersects.length; i++) {
                    const intersect = intersects[i];
                    if (intersect.object.name === 'back-wall') {

                        // check for targe rollover position if there is an object allready
                        let targetX = Math.floor(intersect.point.x / this.activeElement.length) * this.activeElement.length - (62.3 - this.activeElement.length);
                        if (targetX < this.maxAllowedXPosition) {
                            return;
                        }

                        let targetY = 0.5;
                        const targetZ = this.activeElement.width;

                        if (this.activeElement.isEdgeElement) {
                            targetX = -this.activeElement.width;
                        }

                        let targetPosition = new THREE.Vector3(targetX, targetY, targetZ);

                        const shelfSectionNumberX = this.calculateShelfSectionNumberForTargetXPosition(targetX, this.activeElement);

                        if (this.shelfSectionsX.has(shelfSectionNumberX)) {
                            const currentShelfSection = this.shelfSectionsX.get(shelfSectionNumberX);
                            if (currentShelfSection.isClosed && !this.eraserIsActive) {
                                this.rolloverMesh.visible = false;
                                return;
                            }
                        }

                        // allow only edge elements in the edge space
                        if (shelfSectionNumberX === this.numberOfshelfSectionsInX) {
                            if (! (this.activeElement.isEdgeElement || this.eraserIsActive)) {
                                this.rolloverMesh.visible = false;
                                return;
                            }
                        }



                        let elementOnTargetPosition = this.checkIfThereisAnObjectOnTargetPosition(targetPosition);
                        const elementOnEdgePosition = this.checkIfTargetPositionIsOnEdge(targetPosition);

                        let elementOnFloorFound = false;
                        if (elementOnTargetPosition != null) {
                            elementOnFloorFound = true;
                            this.markedObject = elementOnTargetPosition;
                        }

                        if (this.eraserIsActive) {
                            if (elementOnFloorFound) {
                                while (elementOnTargetPosition !== null) {
                                    const boundingBoxOnTargetPosition = new THREE.Box3().setFromObject(elementOnTargetPosition)
                                    targetY = boundingBoxOnTargetPosition.max.y;
                                    targetPosition.y = targetY;
                                    this.markedObject = elementOnTargetPosition;
                                    elementOnTargetPosition = this.checkIfThereisAnObjectOnTargetPosition(targetPosition);
                                }
                                this.eraser3dObject.visible =  true;
                            } else {
                                this.eraser3dObject.visible =  false;
                            }
                            targetX = targetX + 10 ; // small offset for displayed position
                            this.eraser3dObject.position.set(targetX, targetY, 10);
                        } else {
                            this.markedObject = null;
                            this.rolloverMeshSideWall.visible = false;
                            this.rolloverMesh.visible = true;

                            if (this.activeElement.isFloorElement) {
                                if (elementOnTargetPosition) {
                                    this.rolloverMesh.visible = false;
                                } else {
                                    if (this.activeElement.isEdgeElement) {
                                        if (elementOnEdgePosition) {
                                            this.rolloverMesh.visible = true;
                                        }
                                    } else {
                                        this.rolloverMesh.visible = true;
                                    }
                                }
                            } else {
                                if (elementOnFloorFound) {
                                    this.rolloverMesh.visible = true;
                                } else {
                                    this.rolloverMesh.visible = false;
                                }
                                while (elementOnTargetPosition !== null) {
                                    const boundingBoxOnTargetPosition = new THREE.Box3().setFromObject(elementOnTargetPosition)
                                    targetY = boundingBoxOnTargetPosition.max.y;
                                    targetPosition.y = targetY;

                                    elementOnTargetPosition = this.checkIfThereisAnObjectOnTargetPosition(targetPosition);
                                }
                            }
                            this.rolloverMesh.position.set(targetX, targetY, targetZ);
                        }
                    }

                    if (intersect.object.name === 'side-wall') {
                        this.rolloverMesh.visible = false;
                        this.rolloverMeshSideWall.visible = true;

                        // check for targe rollover position if there is an object allready
                        let targetX = -this.activeElement.length + 20;
                        let targetY = 0.5;
                        const targetZ =  Math.floor(intersect.point.z / this.activeElement.length) * this.activeElement.length + (62.3 - this.activeElement.length);
                        if (targetZ > this.maxAllowedZPosition) {
                            return;
                        }
                        if (targetZ < this.elementWidthInX) {
                            return;
                        }

                        const targetPosition = new THREE.Vector3(targetX, targetY, targetZ);

                        const shelfSectionNumberZ = this.calculateShelfSectionNumberForTargetZPosition(targetZ);
                        if (this.shelfSectionsZ.has(shelfSectionNumberZ)) {
                            const currentShelfSection = this.shelfSectionsZ.get(shelfSectionNumberZ);
                            if (currentShelfSection.isClosed && !this.eraserIsActive) {
                                this.rolloverMesh.visible = false;
                                return;
                            }
                        }

                        let elementOnTargetPositionSideWall = this.checkIfThereisAnObjectOnTargetPositionSideWall(targetPosition);

                        let elementOnFloorFoundSideWall = false;
                        if (elementOnTargetPositionSideWall != null) {
                            elementOnFloorFoundSideWall = true;
                            this.markedObject = elementOnTargetPositionSideWall;

                        }

                        if (this.eraserIsActive) {
                            if (elementOnFloorFoundSideWall) {
                                while (elementOnTargetPositionSideWall !== null) {
                                    const boundingBoxOnTargetPositionSideWall = new THREE.Box3().setFromObject(elementOnTargetPositionSideWall)
                                    targetY = boundingBoxOnTargetPositionSideWall.max.y;
                                    targetPosition.y = targetY;
                                    this.markedObject = elementOnTargetPositionSideWall;
                                    elementOnTargetPositionSideWall = this.checkIfThereisAnObjectOnTargetPositionSideWall(targetPosition);
                                }
                                this.eraser3dObject.visible =  true;
                            } else {
                                this.eraser3dObject.visible =  false;
                            }
                            this.eraser3dObject.position.set(targetX, targetY, targetZ);
                        } else {

                            if (this.activeElement.isFloorElement) {
                                if (elementOnTargetPositionSideWall) {
                                    this.rolloverMeshSideWall.visible = false;
                                } else {
                                    if (this.activeElement.isEdgeElement) {
                                        this.rolloverMeshSideWall.visible = false;
                                    } else {
                                        this.rolloverMeshSideWall.visible = true;
                                    }
                                }
                            } else {
                                if (elementOnFloorFoundSideWall) {
                                    this.rolloverMeshSideWall.visible = true;
                                } else {
                                    this.rolloverMeshSideWall.visible = false;
                                }
                                while (elementOnTargetPositionSideWall !== null) {
                                    const boundingBoxOnTargetPositionSideWall = new THREE.Box3().setFromObject(elementOnTargetPositionSideWall)
                                    targetY = boundingBoxOnTargetPositionSideWall.max.y;
                                    targetPosition.y = targetY;
                                    this.markedObject = elementOnTargetPositionSideWall;
                                    if (elementOnTargetPositionSideWall.isTopElement) {
                                        // top element found, cant add nothing on top
                                        this.rolloverMeshSideWall.visible = false;
                                        elementOnTargetPositionSideWall = null;
                                    } else {
                                        elementOnTargetPositionSideWall = this.checkIfThereisAnObjectOnTargetPositionSideWall(targetPosition);
                                    }
                                }
                            }
                            this.rolloverMeshSideWall.position.set(targetX, targetY, targetZ);
                        }
                    }
                }
            }

            this.render();
        }
    }

    /**
     *
     * @param number targetX
     * @param ShelfElement activeElement
     * @return number
     */
    private calculateShelfSectionNumberForTargetXPosition(targetX, activeElement) {
        if (!this.activeElement.isEdgeElement) {
            if (targetX < this.maxAllowedXPosition || targetX >= 0 ) return;
            return (this.numberOfshelfSectionsInX + 1) + Math.floor(targetX / this.elementWidthInX) + 1;
        } else {
            return this.numberOfshelfSectionsInX;
        }

    }

    /**
     *
     * @param number targetX
     * @return number
     */
    private calculateShelfSectionNumberForTargetZPosition(targetZ) {
        if (targetZ > this.maxAllowedZPosition || targetZ <= 0 ) return;
        return Math.floor(targetZ / this.elementWidthInX);
    }

    private checkIfTargetPositionIsOnEdge(targetPosition) {
        if (targetPosition.x > -70) {
            return true;
        }
        return false;
    }

    /**
     * will return object on possion, false otherwise
     * @param targetPosition
     * @returns boolean
     */
    private checkIfThereisAnObjectOnTargetPosition(targetPosition) {

        for (let k = 0; k < this.insertedElements.length; k++) {
            const insertedElement = this.insertedElements[k];
            const boundingBox = new THREE.Box3().setFromObject(insertedElement)

            const inXRange = (boundingBox.min.x < targetPosition.x + 1 && (targetPosition.x + 1) < boundingBox.max.x );
            const inYRange = (boundingBox.min.y < targetPosition.y + 1 && targetPosition.y + 1 < boundingBox.max.y);
            const inZRange = (boundingBox.min.z < targetPosition.z && targetPosition.z  < boundingBox.max.z + 1);
            if (inXRange && inYRange && inZRange) {
                return insertedElement;
            }
        }
        return null;
    }


    /**
     * will return object on possion, false otherwise
     * @param targetPosition
     * @returns bounding box or null
     */
    private checkIfThereisAnObjectOnTargetPositionSideWall(targetPosition) {

        for (let k = 0; k < this.insertedElementsSideWall.length; k++) {
            const insertedElement = this.insertedElementsSideWall[k];
            const boundingBox = new THREE.Box3().setFromObject(insertedElement)
            const inXRange = (boundingBox.min.x < targetPosition.x + 1 && (targetPosition.x + 1) < boundingBox.max.x );
            const inYRange = (boundingBox.min.y < targetPosition.y + 1 && targetPosition.y + 1  < boundingBox.max.y );
            const inZRange = (boundingBox.min.z < targetPosition.z && targetPosition.z + 1  < boundingBox.max.z);

            if (inXRange && inYRange && inZRange) {
                return insertedElement;
            }
        }
        return null;
    }


    public showLeftWall() {
        if (this.scene !== undefined && ! this.leftWallView) {
            this.leftWallView = true;
            this.rightWallView = false;

            this.createSceneAndRender();

            this.camera.lookAt( - this.roomLength * 1.2 , this.roomHeight / 2, 0);

            if (this.roomLength > 7) {
                this.camera.position.set( -this.roomLength * 0.7 , this.roomHeight / 2, Math.max(this.roomLength, this.roomWidth) * 1.1 );
            } else if (this.roomLength > 5) {
                this.camera.position.set( -this.roomLength * 0.7 , this.roomHeight / 2, Math.max(this.roomLength, this.roomWidth) * 1.2 );
            } else {
                this.camera.position.set( -this.roomLength / 2 , this.roomHeight / 2, Math.max(this.roomLength, this.roomWidth) * 2 );
            }
            this.render();
        }
    }

    public showRightWall() {
        if (this.scene !== undefined && ! this.rightWallView) {
            this.rightWallView = true;
            this.leftWallView = false;

            this.createSceneAndRender();

            this.camera.lookAt( 0 , this.roomHeight / 2, this.roomWidth * 1.2);
            if (this.roomWidth > 7) {
                this.camera.position.set( -Math.max(this.roomLength, this.roomWidth) * 1.1 , this.roomHeight / 2, this.roomWidth * 0.7 );
            } else if (this.roomWidth > 5) {
                this.camera.position.set( -Math.max(this.roomLength, this.roomWidth) * 1.2 , this.roomHeight / 2, this.roomWidth * 0.7 );
            } else {
                this.camera.position.set(  - Math.max(this.roomLength, this.roomWidth) * 2  , this.roomHeight / 2, this.roomWidth * 0.5 );
            }
            this.render();
        }
    }

    public showElementList() {
        this.dataService.shareData('elementCountList', this.elementCountList);
        this.elementListComponent.updateElementList();
        this.elementListOverlay.className = 'show';
    }

    public closeOverlay() {
        this.elementListOverlay.className = 'hidden';
        this.onFormPage = false;
    }


    public backToList() {
        this.onFormPage = false;
    }

    public submitForm() {

        const customer = this.dataService.getSharedData('customer');
        const elements = this.dataService.getSharedData('elementCountList');

        if (customer === undefined) {
          this.validationErrors = true;
          return;
        }

        if (customer.company === undefined) {
            this.validationErrors = true;
            return;
        }
        if (customer.name === undefined) {
            this.validationErrors = true;
            return;
        }
        if (customer.street === undefined) {
            this.validationErrors = true;
            return;
        }
        if (customer.postCode === undefined) {
            this.validationErrors = true;
            return;
        }
        if (customer.city === undefined) {
            this.validationErrors = true;
            return;
        }
        if (customer.phone === undefined) {
            this.validationErrors = true;
            return;
        }
        if (customer.email === undefined) {
            this.validationErrors = true;
            return;
        }

        if (!customer.consentDeclared) {
            this.validationErrors = true;
            return;
        }

        // load list with all shelf elements!
        this.getShelfElements();


      const kklData = {
            'customer': customer,
            'pieceList': elements,
            'shelfElements': this.shelfElements,
            'shelfSectionsX': this.shelfSectionsX,
            'shelfSectionsZ': this.shelfSectionsZ,
            'images': []
        }
        this.showRoom();
        this.render();
        let pngUrl = this.canvas.toDataURL();
        kklData.images.push(pngUrl);
        this.showLeftWall();
        this.render();
        pngUrl = this.canvas.toDataURL();
        kklData.images.push(pngUrl);
        this.showRightWall();
        this.render();
        pngUrl = this.canvas.toDataURL();
        kklData.images.push(pngUrl);

        let appToken;

        if (!this.formSubmitSent) {
            this.kklRestService.login({
                username: 'kkl-app-user',
                password: '990##asdplüp34!349AK91'
            }).subscribe(data => {
                // display its headers
                appToken = data.token;
                this.postKKLdata(kklData, appToken);
            });
        }

        this.formSubmitSent = true;

    }


    private postKKLdata(kklData: object, token: string) {
        this.kklRestService.kkl(kklData, token).subscribe(data => {
            this.logout(token);
            this.showConfirmation = true;
        })

    }

    private logout(token: string) {
        this.kklRestService.logout(token).subscribe(data => {
        })
    }

    public showForm() {
        this.onFormPage = true;
    }

    public finishApp() {
       window.location.reload();
    }

    public printList() {
        window.print();
    }



}



// WEBPACK FOOTER //
// ./src/app/scene/scene.component.ts